import React, { useEffect, useRef } from 'react';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { LkpLoading } from 'components';
import { CalendarContainer, CalendarContentWrapper, CalendarToolBar, CalendarLoadingOverlay } from './styled';
import { CalendarToolbar } from './components/CalendarToolbar';
import { CalendarFilters } from './components/CalendarFilters';
import { WeekView } from './components/WeekView';
import { DayView } from './components/DayView';
import { MonthView } from './components/MonthView';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';
import { CalendarView, ViewOption } from '../../hooks/useCalendarNavigation';
import { CalendarEvent, EventsByDate } from '../../utils/eventUtils';
import { DisplayMode } from '../../hooks/useCalendarFilters';
import { EventColorSet } from '../../utils/eventColors';

interface CalendarProps {
  setExecutoresSetoresModalOpen: (open: boolean) => void;
  setAmbientesEquipamentosModalOpen: (open: boolean) => void;
  setPlanosModalOpen: (open: boolean) => void;
  currentDate: Date;
  view: CalendarView;
  viewsOptions: ViewOption[];
  handleViewChange: (value: string | number | null) => void;
  goToToday: () => void;
  navigate: (direction: 'prev' | 'next') => void;
  navigateByDay: (direction: 'prev' | 'next') => void;
  weekDays: Date[];
  type: 'pendentes' | 'baixadas';
  setType: (type: 'pendentes' | 'baixadas') => void;
  planosSelectionLabel: string;
  executoresSetoresSelectionLabel: string;
  ambientesEquipamentosSelectionLabel: string;
  maintenanceUnitsOptions: { label: string; value: string }[];
  maintenanceUnitsValues: string[];
  onMaintenanceUnitsChange: (values: unknown) => void;
  openFilters: boolean;
  setOpenFilters: (open: boolean) => void;
  hasActiveFilters: boolean;
  clearAllFilters: () => void;
  events: EventsByDate;
  isLoadingEvents: boolean;
  fetchAgendaTasks: (isWeekView?: boolean) => Promise<void>;
  onCardClick: (event: CalendarEvent, colorSet: EventColorSet) => void;
  loadMoreOccurrences: () => Promise<void>;
  hasMoreOccurrences: boolean;
  displayMode: DisplayMode;
  setDisplayMode: (mode: DisplayMode) => void;
  canViewOccurrences: boolean;
}

export const Calendar: React.FC<CalendarProps> = ({
  setExecutoresSetoresModalOpen,
  setAmbientesEquipamentosModalOpen,
  setPlanosModalOpen,
  currentDate,
  view,
  viewsOptions,
  handleViewChange,
  goToToday,
  navigate,
  navigateByDay,
  weekDays,
  type,
  setType,
  planosSelectionLabel,
  executoresSetoresSelectionLabel,
  ambientesEquipamentosSelectionLabel,
  maintenanceUnitsOptions,
  maintenanceUnitsValues,
  onMaintenanceUnitsChange,
  openFilters,
  setOpenFilters,
  hasActiveFilters,
  clearAllFilters,
  events,
  isLoadingEvents,
  fetchAgendaTasks,
  onCardClick,
  loadMoreOccurrences,
  hasMoreOccurrences,
  displayMode,
  setDisplayMode,
  canViewOccurrences,
}) => {
  const {
    events: calendarEvents,
    horasPrevistasByDate,
    handleDragEndEvent,
  } = useCalendarEvents({
    initialEvents: events,
    fetchAgendaTasks,
    isWeekView: view === CalendarView.Semana,
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const filtersButtonLabel = openFilters ? 'Ocultar Filtros' : 'Exibir Filtros';
  const showClearFilters = openFilters && hasActiveFilters;

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMoreOccurrences) return;
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMoreOccurrences();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMoreOccurrences, loadMoreOccurrences]);

  return (
    <CalendarContainer>
      <CalendarToolBar>
        <CalendarToolbar
          currentDate={currentDate}
          view={view}
          viewsOptions={viewsOptions}
          onViewChange={handleViewChange}
          onNavigate={navigate}
          onGoToToday={goToToday}
          openFilters={openFilters}
          onToggleFilters={() => setOpenFilters(!openFilters)}
          filtersButtonLabel={filtersButtonLabel}
          showClearFilters={showClearFilters}
          onClearFilters={clearAllFilters}
        />
        {openFilters && (
          <CalendarFilters
            type={type}
            onTypeChange={setType}
            onOpenExecutoresModal={() => setExecutoresSetoresModalOpen(true)}
            onOpenAmbientesModal={() => setAmbientesEquipamentosModalOpen(true)}
            onOpenPlanosModal={() => setPlanosModalOpen(true)}
            planosSelectionLabel={planosSelectionLabel}
            executoresSetoresSelectionLabel={executoresSetoresSelectionLabel}
            ambientesEquipamentosSelectionLabel={ambientesEquipamentosSelectionLabel}
            maintenanceUnitsOptions={maintenanceUnitsOptions}
            maintenanceUnitsValues={maintenanceUnitsValues}
            onMaintenanceUnitsChange={onMaintenanceUnitsChange}
            displayMode={displayMode}
            onDisplayModeChange={setDisplayMode}
            canViewOccurrences={canViewOccurrences}
          />
        )}
      </CalendarToolBar>

      <CalendarContentWrapper>
        {isLoadingEvents && (
          <CalendarLoadingOverlay data-testid="calendar-loading-overlay">
            <LkpLoading />
          </CalendarLoadingOverlay>
        )}
        <DndContext sensors={sensors} onDragEnd={handleDragEndEvent} collisionDetection={closestCenter}>
          {view === CalendarView.Semana && (
            <WeekView
              weekDays={weekDays}
              events={calendarEvents}
              horasPrevistasByDate={horasPrevistasByDate}
              isCompletedView={type === 'baixadas'}
              onNavigateByDay={navigateByDay}
              onCardClick={onCardClick}
            />
          )}
          {view === CalendarView.Dia && (
            <DayView
              currentDate={currentDate}
              events={calendarEvents}
              horasPrevistasByDate={horasPrevistasByDate}
              isCompletedView={type === 'baixadas'}
              onCardClick={onCardClick}
              onNavigate={navigate}
            />
          )}
          {view === CalendarView.Mes && (
            <MonthView
              currentDate={currentDate}
              events={calendarEvents}
              horasPrevistasByDate={horasPrevistasByDate}
              isCompletedView={type === 'baixadas'}
              onCardClick={onCardClick}
              onNavigate={navigate}
            />
          )}
        </DndContext>
        <div ref={sentinelRef} style={{ height: 1 }} />
      </CalendarContentWrapper>
    </CalendarContainer>
  );
};
